import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetBankDocumentEntriesQueueQuery,
  useLazyGetBankDocumentByIdQuery,
  usePostBankDocumentEntryMutation,
  useRegisterSupplierPrepaymentMutation,
  useUnpostBankDocumentEntryMutation,
  useUpdateBankDocumentEntryMutation,
} from '../Service/bankDocumentsService';
import { useAuthUser } from '../../Auth/hooks/useAuthHooks';
import {
  buildPostBankDocumentEntryRequest,
  buildRegisterSupplierPrepaymentRequest,
  buildUnpostBankDocumentEntryRequest,
  buildUpdateBankDocumentEntryRequest,
} from '../Service/bankDocumentsRequestBuilder';
import { getBankDocumentsErrorMessage } from '../Utils/bankDocumentsErrorMapping';
import { BankDocumentEntryModel } from '../Model/bankDocument.model';
import { AssignEntryDialogResult } from '../Components/AssignEntryDialog';
import type {
  BankDocumentEntryQueueDto,
  BankDocumentQueueTargetType,
} from '../Types/bankDocument.types';
import { mapBankDocumentEntryDtoToModel } from '../Mapping/bankDocumentsMapper';
import {
  isQueueEntryReadyToPost,
  resolveBankDocumentQueueState,
} from '../Mapping/bankDocumentQueueStatusMapper';
import { bankDocumentsLogger } from '../Utils/bankDocumentsLogger';

type QueueDiagnosticFilter = 'failed' | 'posted' | 'all';
const DEFAULT_QUEUE_PAGE_SIZE = 50;
const MAX_QUEUE_PAGE_SIZE = 200;
const MIN_QUEUE_PAGE_NUMBER = 1;
const FILTERS_STORAGE_KEY_PREFIX = 'bankDocuments.queue.filters';

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createDefaultDateRange = (): { dateFrom: string; dateTo: string } => {
  const now = new Date();
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(now.getDate() - 7);

  return {
    dateFrom: toDateInputValue(sevenDaysAgo),
    dateTo: toDateInputValue(now),
  };
};

interface QueueFiltersState {
  dateFrom: string;
  dateTo: string;
  postedState: 'posted' | 'unposted';
  diagnosticFilter: QueueDiagnosticFilter;
  withUnassignedRemainder: boolean;
  companyId?: number;
  bankAccountId?: number;
  targetType?: BankDocumentQueueTargetType;
  amountDirection?: 'inflow' | 'outflow';
  counterpartyName?: string;
  title?: string;
}

const createDefaultFilters = (): QueueFiltersState => ({
  ...createDefaultDateRange(),
  postedState: 'unposted',
  diagnosticFilter: 'failed',
  withUnassignedRemainder: false,
  companyId: undefined,
  bankAccountId: undefined,
  targetType: undefined,
  amountDirection: undefined,
  counterpartyName: undefined,
  title: undefined,
});

const isValidDiagnosticFilter = (value: unknown): value is QueueDiagnosticFilter =>
  value === 'failed' || value === 'posted' || value === 'all';

const shouldRotateIdempotencyKeyOnFailure = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const status = (error as { status?: unknown }).status;
  if (
    status === 'FETCH_ERROR' ||
    status === 'TIMEOUT_ERROR' ||
    status === 'PARSING_ERROR' ||
    status === 'CUSTOM_ERROR'
  ) {
    return false;
  }

  if (typeof status === 'number') {
    return status >= 400 && status < 500;
  }

  return false;
};

let fallbackIdempotencyCounter = 0;

const createIdempotencyKey = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const toHex = (value: number): string => value.toString(16).padStart(2, '0');
    const hex = Array.from(bytes, toHex);
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  fallbackIdempotencyCounter += 1;
  const randomHex = Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, '0');
  const timestampHex = Date.now().toString(16).slice(-8).padStart(8, '0');
  const counterHex = `${timestampHex}${randomHex}${fallbackIdempotencyCounter.toString(16)}`
    .slice(-12)
    .padStart(12, '0');
  return `00000000-0000-4000-8000-${counterHex}`;
};

const parseStoredFilters = (serialized: string | null): QueueFiltersState | null => {
  if (!serialized) {
    return null;
  }

  try {
    const parsed = JSON.parse(serialized) as Partial<QueueFiltersState>;
    const defaults = createDefaultFilters();

    return {
      dateFrom: typeof parsed.dateFrom === 'string' ? parsed.dateFrom : defaults.dateFrom,
      dateTo: typeof parsed.dateTo === 'string' ? parsed.dateTo : defaults.dateTo,
      postedState: parsed.postedState === 'posted' ? 'posted' : 'unposted',
      diagnosticFilter: isValidDiagnosticFilter(parsed.diagnosticFilter)
        ? parsed.diagnosticFilter
        : defaults.diagnosticFilter,
      withUnassignedRemainder:
        typeof parsed.withUnassignedRemainder === 'boolean'
          ? parsed.withUnassignedRemainder
          : defaults.withUnassignedRemainder,
      companyId: typeof parsed.companyId === 'number' ? parsed.companyId : undefined,
      bankAccountId: typeof parsed.bankAccountId === 'number' ? parsed.bankAccountId : undefined,
      targetType:
        parsed.targetType === 'invoice' ||
        parsed.targetType === 'reservation' ||
        parsed.targetType === 'expenseInvoice' ||
        parsed.targetType === 'unassigned'
          ? parsed.targetType
          : undefined,
      amountDirection:
        parsed.amountDirection === 'inflow' || parsed.amountDirection === 'outflow'
          ? parsed.amountDirection
          : undefined,
      counterpartyName:
        typeof parsed.counterpartyName === 'string' ? parsed.counterpartyName : undefined,
      title: typeof parsed.title === 'string' ? parsed.title : undefined,
    };
  } catch {
    return null;
  }
};

export const useBankDocumentQueuePage = (options?: {
  returnContext?: { entryId: number; bankDocumentId: number; reservationId: number } | null;
}) => {
  const authUser = useAuthUser();
  const [filters, setFilters] = useState<QueueFiltersState>(() => createDefaultFilters());
  const [postingDescription, setPostingDescription] = useState('');
  const [pageNumber, setPageNumber] = useState(MIN_QUEUE_PAGE_NUMBER);
  const pageSize = DEFAULT_QUEUE_PAGE_SIZE;
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editEntry, setEditEntry] = useState<BankDocumentEntryModel | null>(null);
  const [editEntryCompanyId, setEditEntryCompanyId] = useState<number | null>(null);
  const [initialDraftReservationId, setInitialDraftReservationId] = useState<number | null>(null);
  const autoOpenAttemptedRef = useRef(false);
  const queueLoadStartedAtRef = useRef<number | null>(null);
  const loadMetricLoggedRef = useRef(false);
  const idempotencyKeysRef = useRef<Record<string, string>>({});

  const filtersStorageKey = useMemo(() => {
    const userPart = authUser?.id ? authUser.id.toString() : 'anonymous';
    return `${FILTERS_STORAGE_KEY_PREFIX}.${userPart}`;
  }, [authUser?.id]);

  useEffect(() => {
    const storedFilters = parseStoredFilters(localStorage.getItem(filtersStorageKey));

    if (!storedFilters) {
      return;
    }

    setFilters(storedFilters);
  }, [filtersStorageKey]);

  useEffect(() => {
    localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
  }, [filters, filtersStorageKey]);

  const getIdempotencyKeyKey = (action: 'post' | 'unpost', entryId: number): string =>
    `${action}:${entryId}`;

  const getOrCreateIdempotencyKey = (action: 'post' | 'unpost', entryId: number): string => {
    const storageKey = getIdempotencyKeyKey(action, entryId);
    const existing = idempotencyKeysRef.current[storageKey];

    if (existing) {
      return existing;
    }

    const nextKey = createIdempotencyKey();
    idempotencyKeysRef.current[storageKey] = nextKey;
    return nextKey;
  };

  const clearIdempotencyKey = (action: 'post' | 'unpost', entryId: number): void => {
    const storageKey = getIdempotencyKeyKey(action, entryId);
    delete idempotencyKeysRef.current[storageKey];
  };

  const resolvedPostedState = useMemo(() => {
    if (filters.diagnosticFilter === 'failed') {
      return 'unposted' as const;
    }

    if (filters.diagnosticFilter === 'posted') {
      return 'posted' as const;
    }

    if (filters.diagnosticFilter === 'all') {
      return undefined;
    }

    return filters.postedState;
  }, [filters.diagnosticFilter, filters.postedState]);

  const queueQuery = useGetBankDocumentEntriesQueueQuery({
    companyId: filters.companyId,
    bankAccountId: filters.bankAccountId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    postedState: resolvedPostedState,
    targetType: filters.targetType,
    amountDirection: filters.amountDirection,
    withUnassignedRemainder: filters.withUnassignedRemainder,
    counterpartyName: filters.counterpartyName,
    title: filters.title,
    pageNumber:
      filters.diagnosticFilter === 'failed'
        ? MIN_QUEUE_PAGE_NUMBER
        : Math.max(MIN_QUEUE_PAGE_NUMBER, pageNumber),
    pageSize:
      filters.diagnosticFilter === 'failed'
        ? MAX_QUEUE_PAGE_SIZE
        : Math.min(MAX_QUEUE_PAGE_SIZE, Math.max(1, pageSize)),
  });

  useEffect(() => {
    if (queueQuery.isFetching && queueLoadStartedAtRef.current === null) {
      queueLoadStartedAtRef.current = performance.now();
      loadMetricLoggedRef.current = false;
    }

    if (
      !queueQuery.isFetching &&
      queueLoadStartedAtRef.current !== null &&
      !loadMetricLoggedRef.current
    ) {
      const elapsedMs = Math.round(performance.now() - queueLoadStartedAtRef.current);
      bankDocumentsLogger.debug('[BankDocuments][Metrics] Queue load time', {
        elapsedMs,
        totalCount: queueQuery.data?.totalCount ?? 0,
        pageNumber,
        pageSize,
      });

      queueLoadStartedAtRef.current = null;
      loadMetricLoggedRef.current = true;
    }
  }, [pageNumber, pageSize, queueQuery.data?.totalCount, queueQuery.isFetching]);

  const [postEntry, { isLoading: isPostingSingle }] = usePostBankDocumentEntryMutation();
  const [unpostEntry, { isLoading: isUnpostingSingle }] = useUnpostBankDocumentEntryMutation();
  const [updateEntry, { isLoading: isAssigning }] = useUpdateBankDocumentEntryMutation();
  const [loadBankDocumentById, { isFetching: isLoadingAssignEntry }] =
    useLazyGetBankDocumentByIdQuery();
  const [registerSupplierPrepayment, { isLoading: isRegisteringSupplierPrepayment }] =
    useRegisterSupplierPrepaymentMutation();

  const queueItems = useMemo(() => {
    const entries = queueQuery.data?.items ?? [];

    if (filters.diagnosticFilter === 'failed') {
      return entries.filter((item) => {
        const queueState = resolveBankDocumentQueueState(item);
        return (
          queueState.status === 'blocked' ||
          (queueState.status === 'unknown' && !queueState.usedLegacyFallback && !item.isPosted)
        );
      });
    }

    if (filters.diagnosticFilter === 'posted') {
      return entries.filter((item) => item.isPosted);
    }

    return entries;
  }, [filters.diagnosticFilter, queueQuery.data?.items]);

  useEffect(() => {
    const entries = queueQuery.data?.items ?? [];
    const unknownEntries = entries
      .map((item) => ({
        item,
        state: resolveBankDocumentQueueState(item),
      }))
      .filter(({ state }) => state.status === 'unknown');

    if (unknownEntries.length === 0) {
      return;
    }

    bankDocumentsLogger.warn('[BankDocuments][QueueStatus] Unknown status fallback applied', {
      entries: unknownEntries.map(({ item }) => ({
        entryId: item.entryId,
        queueStatus: item.queueStatus ?? null,
        postingStatus: item.postingStatus,
        diagnosticCode: item.diagnosticCode ?? null,
      })),
    });
  }, [queueQuery.data?.items]);

  const effectiveTotalCount = useMemo(() => {
    if (filters.diagnosticFilter === 'failed') {
      return queueItems.length;
    }

    return queueQuery.data?.totalCount ?? 0;
  }, [filters.diagnosticFilter, queueItems.length, queueQuery.data?.totalCount]);

  const effectiveTotalPages = useMemo(() => {
    if (filters.diagnosticFilter === 'failed') {
      return 1;
    }

    return queueQuery.data?.totalPages ?? 1;
  }, [filters.diagnosticFilter, queueQuery.data?.totalPages]);

  const isProcessing =
    isPostingSingle ||
    isUnpostingSingle ||
    isAssigning ||
    isRegisteringSupplierPrepayment ||
    isLoadingAssignEntry;

  const setFilter = <T extends keyof typeof filters>(key: T, value: (typeof filters)[T]) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
    setPageNumber(1);
  };

  useEffect(() => {
    const maxPage = Math.max(
      MIN_QUEUE_PAGE_NUMBER,
      queueQuery.data?.totalPages ?? MIN_QUEUE_PAGE_NUMBER
    );
    if (pageNumber > maxPage) {
      setPageNumber(maxPage);
    }
  }, [pageNumber, queueQuery.data?.totalPages]);

  const handleOpenAssignDialog = async (entry: Pick<BankDocumentEntryQueueDto, 'entryId' | 'bankDocumentId'>): Promise<boolean> => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const bankDocument = await loadBankDocumentById(entry.bankDocumentId).unwrap();
      const fullEntry = bankDocument.entries.find((item) => item.id === entry.entryId);

      if (!fullEntry) {
        throw new Error(`BANK_DOCUMENT_ENTRY_NOT_FOUND:${entry.entryId}`);
      }

      setEditEntry(mapBankDocumentEntryDtoToModel(fullEntry, bankDocument.currency ?? 'PLN'));
      setEditEntryCompanyId(bankDocument.companyId ?? null);
      return true;
    } catch (error) {
      setEditEntry(null);
      setEditEntryCompanyId(null);
      setInitialDraftReservationId(null);
      setErrorMessage(
        getBankDocumentsErrorMessage(
          error,
          `Nie udało się wczytać pełnych danych pozycji ${entry.entryId} do edycji.`
        )
      );
      return false;
    }
  };

  const handleCloseAssignDialog = () => {
    setEditEntry(null);
    setEditEntryCompanyId(null);
    setInitialDraftReservationId(null);
  };

  useEffect(() => {
    const ctx = options?.returnContext;
    if (!ctx || autoOpenAttemptedRef.current) {
      return;
    }
    autoOpenAttemptedRef.current = true;
    handleOpenAssignDialog({ entryId: ctx.entryId, bankDocumentId: ctx.bankDocumentId }).then(
      (succeeded) => {
        if (succeeded) {
          setInitialDraftReservationId(ctx.reservationId);
        }
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.returnContext]);

  const handleAssignSave = async (result: AssignEntryDialogResult) => {
    if (!editEntry) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);

    if (result.assignmentType === 'supplierPrepayment') {
      if (!authUser?.id) {
        setErrorMessage('Brak użytkownika do rejestracji zaliczki dostawcy.');
        return;
      }

      if (!result.supplierApContractorId) {
        setErrorMessage('Wybierz kontrahenta dostawcy przed rejestracją zaliczki.');
        return;
      }

      try {
        await registerSupplierPrepayment(
          buildRegisterSupplierPrepaymentRequest({
            entryId: editEntry.id,
            userId: authUser.id,
            supplierApContractorId: result.supplierApContractorId,
            description: postingDescription,
          })
        ).unwrap();

        setSuccessMessage(`Zarejestrowano zaliczkę dostawcy dla pozycji ${editEntry.id}.`);
        handleCloseAssignDialog();
        await queueQuery.refetch();
      } catch (error) {
        setErrorMessage(
          getBankDocumentsErrorMessage(
            error,
            'Nie udało się zarejestrować zaliczki dostawcy dla wybranej pozycji.'
          )
        );
      }

      return;
    }

    try {
      await updateEntry(
        buildUpdateBankDocumentEntryRequest(editEntry.id, editEntry.bankDocumentId, {
          title: editEntry.title,
          counterpartyName: editEntry.counterpartyName,
          counterpartyAccountNumber: editEntry.counterpartyAccountNumber,
          reference: editEntry.reference,
          invoiceId: result.invoiceId ?? null,
          reservationId: result.reservationId ?? null,
          expenseInvoiceId: result.expenseInvoiceId ?? null,
          allocations: result.allocations ?? null,
        })
      ).unwrap();

      setSuccessMessage(`Zaktualizowano przypisanie pozycji ${editEntry.id}.`);
      handleCloseAssignDialog();
      await queueQuery.refetch();
    } catch (error) {
      setErrorMessage(getBankDocumentsErrorMessage(error, 'Nie udało się zapisać przypisania.'));
    }
  };

  const handlePostEntry = async (entryId: number) => {
    if (!authUser?.id) {
      setErrorMessage('Brak użytkownika do księgowania pozycji.');
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    const idempotencyKey = getOrCreateIdempotencyKey('post', entryId);

    try {
      await postEntry(
        buildPostBankDocumentEntryRequest({
          entryId,
          userId: authUser.id,
          idempotencyKey,
        })
      ).unwrap();

      clearIdempotencyKey('post', entryId);
      setSuccessMessage(`Zaksięgowano pozycję ${entryId}.`);
      await queueQuery.refetch();
    } catch (error) {
      if (shouldRotateIdempotencyKeyOnFailure(error)) {
        clearIdempotencyKey('post', entryId);
      }
      bankDocumentsLogger.warn('[BankDocuments][Metrics] Queue post failure', {
        entryId,
      });
      setErrorMessage(
        getBankDocumentsErrorMessage(error, 'Nie udało się zaksięgować wybranej pozycji.')
      );
    }
  };

  const handleUnpostEntry = async (entryId: number, bankDocumentId: number) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    const idempotencyKey = getOrCreateIdempotencyKey('unpost', entryId);

    try {
      await unpostEntry(
        buildUnpostBankDocumentEntryRequest({
          entryId,
          bankDocumentId,
          idempotencyKey,
        })
      ).unwrap();
      clearIdempotencyKey('unpost', entryId);
      setSuccessMessage(`Cofnięto księgowanie pozycji ${entryId}.`);
      await queueQuery.refetch();
    } catch (error) {
      if (shouldRotateIdempotencyKeyOnFailure(error)) {
        clearIdempotencyKey('unpost', entryId);
      }
      setErrorMessage(
        getBankDocumentsErrorMessage(error, 'Nie udało się cofnąć księgowania pozycji.')
      );
    }
  };

  const handlePostReadySubset = async () => {
    if (!authUser?.id) {
      setErrorMessage('Brak użytkownika do księgowania pozycji.');
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);

    const readyItems = queueItems.filter((item) => isQueueEntryReadyToPost(item));

    if (readyItems.length === 0) {
      setErrorMessage('Brak gotowych pozycji do batch księgowania.');
      return;
    }

    let postedCount = 0;
    let failedCount = 0;
    const failedCodes: Record<string, number> = {};

    for (const item of readyItems) {
      try {
        await postEntry(
          buildPostBankDocumentEntryRequest({
            entryId: item.entryId,
            userId: authUser.id,
            idempotencyKey: getOrCreateIdempotencyKey('post', item.entryId),
          })
        ).unwrap();
        clearIdempotencyKey('post', item.entryId);
        postedCount += 1;
      } catch (error) {
        if (shouldRotateIdempotencyKeyOnFailure(error)) {
          clearIdempotencyKey('post', item.entryId);
        }
        failedCount += 1;
        const mappedError = getBankDocumentsErrorMessage(error, 'UNKNOWN_POSTING_ERROR');
        failedCodes[mappedError] = (failedCodes[mappedError] ?? 0) + 1;
      }
    }

    await queueQuery.refetch();

    bankDocumentsLogger.debug('[BankDocuments][Metrics] Queue batch result', {
      readyItemsCount: readyItems.length,
      postedCount,
      failedCount,
      failuresByCode: failedCodes,
    });

    if (failedCount > 0) {
      setErrorMessage(`Batch zakończony częściowo: posted ${postedCount}, failed ${failedCount}.`);
      return;
    }

    setSuccessMessage(`Batch zakończony sukcesem: posted ${postedCount}.`);
  };

  return {
    queueItems,
    isLoading: queueQuery.isLoading || queueQuery.isFetching,
    isError: queueQuery.isError,
    errorMessage,
    successMessage,
    filters,
    setFilter,
    pageNumber,
    setPageNumber,
    pageSize,
    totalCount: effectiveTotalCount,
    totalPages: effectiveTotalPages,
    postingDescription,
    setPostingDescription,
    isProcessing,
    handlePostEntry,
    handleUnpostEntry,
    handlePostReadySubset,
    handleOpenAssignDialog,
    handleCloseAssignDialog,
    handleAssignSave,
    editEntry,
    editEntryCompanyId,
    initialDraftReservationId,
    refetch: queueQuery.refetch,
  };
};
